import { type PropsWithChildren } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';
import { Navbar, NavbarBackLink } from '@garnish/navigation';

// ─────────────────────────────────────────────────────────────────────────────

export const FloatingControls = (props: Props) => {
  const {
    children,
    isLiquidGlassEnabled = false,
    isFullWidthCta = false,
    shouldDisableControls,
    onBackButtonPress,
  } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  // ─── Derived ─────────────────────────────────────────────────────────

  const shouldRenderBackButton = currentBreakpoint.isXS;

  // ─── Styles ──────────────────────────────────────────────────────────

  const ctaContainerStyles = [
    styles.ctaContainer,
    isFullWidthCta ? styles.ctaContainerFullWidth : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  if (!shouldRenderBackButton) {
    return children;
  }

  return (
    <View style={styles.container}>
      <Navbar.LinksGroup isLiquidGlassEnabled={isLiquidGlassEnabled}>
        <NavbarBackLink
          accessibilityLabel={formatMessage(messages.backButtonA11yLabel)}
          disabled={shouldDisableControls}
          onPress={onBackButtonPress}
        />
      </Navbar.LinksGroup>

      <View style={ctaContainerStyles}>{children}</View>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  backButtonA11yLabel: {
    defaultMessage: 'Go back',
    description: 'Floating controls | Back button label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  ctaContainer: {
    flexShrink: 1,
    marginLeft: 'auto',
  },
  ctaContainerFullWidth: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  isLiquidGlassEnabled?: boolean;
  isFullWidthCta?: boolean;
  shouldDisableControls?: boolean;
  onBackButtonPress: () => void;
}>;
